import { Component, computed, input, output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { BUTTONSIZE } from '@constants/buttons';
import { COLOR } from '@constants/colors';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Icon } from '@components/abb-icon/icons';
import { AbbIconComponent } from '../abb-icon/abb-icon.component';
import { OverflowTooltipDirective } from '@directives/overflow-tooltip.directive';

type ButtonVariant =
  | 'clean'
  | 'none'
  | 'primary-rect'
  | 'primary'
  | 'secondary'
  | 'circular';

const circularButtonSizeClassMapper: Record<BUTTONSIZE, string> = {
  [BUTTONSIZE.SIZE20]: 'w-5 h-5 text-[9px]',
  [BUTTONSIZE.SIZE24]: 'w-6 h-6 text-[11px]',
  [BUTTONSIZE.SIZE32]: 'w-8 h-8 text-[13px]',
  [BUTTONSIZE.SIZE36]: 'w-9 h-9 text-[15px]',
  [BUTTONSIZE.SIZE40]: 'w-10 h-10 text-[16px]',
  [BUTTONSIZE.SIZE48]: 'w-12 h-12 text-[20px]',
  [BUTTONSIZE.SIZE60]: 'w-14 h-14 text-[24px]',
};

const circularButtonColorClassMapper: Record<string, string> = {
  [COLOR.ABB_PRIMARY]:
    'bg-abb-primary border-abb-primary hover:bg-abb-primary hover:border-abb-primary text-abb-white stroke-abb-white disabled:bg-abb-primary/20 disabled:border-abb-primary/5 disabled:text-abb-white',
  [COLOR.ABB_BLACK]:
    'bg-abb-black border-abb-black hover:bg-abb-black hover:border-abb-black text-abb-white disabled:bg-abb-black/20 disabled:border-abb-black/5 disabled:text-abb-white',
  [COLOR.ABB_WHITE]:
    'bg-abb-white border-abb-white hover:bg-abb-white hover:border-abb-white text-black disabled:bg-abb-white/20 disabled:border-abb-white/5 disabled:text-black',
  [COLOR.ABB_GRAY_01]:
    'bg-abb-gray-01 border-abb-gray-01 hover:bg-abb-gray-01 hover:border-abb-gray-01 text-abb-white disabled:bg-abb-gray-01/20 disabled:border-abb-gray-01/5 disabled:text-abb-white',
  [COLOR.ABB_GRAY_02]:
    'bg-abb-gray-02 border-abb-gray-02 hover:bg-abb-gray-02 hover:border-abb-gray-02 text-abb-white disabled:bg-abb-gray-02/20 disabled:border-abb-gray-02/5 disabled:text-abb-white',
  [COLOR.ABB_GRAY_03]:
    'bg-abb-gray-03 border-abb-gray-03 hover:bg-abb-gray-03 hover:border-abb-gray-03 text-abb-white disabled:bg-abb-gray-03/20 disabled:border-abb-gray-03/5 disabled:text-abb-white',
  [COLOR.ABB_GRAY_04]:
    'bg-abb-gray-04 border-abb-gray-04 hover:bg-abb-gray-04 hover:border-abb-gray-04 text-abb-white disabled:bg-abb-gray-04/20 disabled:border-abb-gray-04/5 disabled:text-abb-white',
  [COLOR.ABB_GRAY_05]:
    'bg-abb-gray-05 border-abb-gray-05 hover:bg-abb-gray-05 hover:border-abb-gray-05 text-abb-white disabled:bg-abb-gray-05/20 disabled:border-abb-gray-05/5 disabled:text-abb-white',
  [COLOR.ABB_GRAY_06]:
    'bg-abb-gray-06 border-abb-gray-06 hover:bg-abb-gray-06 hover:border-abb-gray-06 text-abb-white disabled:bg-abb-gray-06/20 disabled:border-abb-gray-06/5 disabled:text-abb-white',
};

const buttonClassMapper: Record<ButtonVariant, string> = {
  clean:
    'w-[inherit] text-nowrap font-bold text-abb-primary transition-colors hover:text-abb-primary-dark',
  primary:
    'w-[inherit] text-nowrap font-bold shadow-sm shadow-abb-black/[0.43] bg-abb-primary border-2 border-abb-primary transition-colors duration-200 hover:bg-abb-primary-dark hover:border-abb-primary-dark transition-colors text-abb-white px-3 rounded-full py-1',
  secondary:
    'w-[inherit] text-nowrap font-bold shadow-sm shadow-abb-black/[0.43] bg-abb-white border-2 border-abb-white transition-colors duration-200 hover:bg-abb-white-dark/20 hover:border-abb-white-dark/0 transition-colors text-abb-black/80 px-3 rounded-full py-1',
  'primary-rect':
    'w-[inherit] text-nowrap font-bold bg-abb-primary border-2 border-abb-primary transition-colors duration-200 hover:bg-abb-primary-dark hover:border-abb-primary-dark text-abb-white px-3 rounded py-1',
  circular:
    'border-2 transition-colors duration-200 p-3 rounded-full justify-center',
  none: '',
};

const disabledButtonClassMapper: Record<ButtonVariant, string> = {
  clean: 'w-[inherit] text-nowrap font-bold text-muted-600 cursor-not-allowed',
  primary:
    'w-[inherit] text-nowrap font-bold shadow-sm shadow-abb-gray-02/20 bg-abb-primary/20 border-2 border-abb-primary/0 text-abb-white cursor-not-allowed px-3 py-1 rounded-full',
  secondary:
    'w-[inherit] text-nowrap font-bold shadow-sm shadow-abb-gray-02/20 bg-abb-white/20 border-2 border-abb-white/20 text-abb-black/20 cursor-not-allowed px-3 py-1 rounded-full',
  'primary-rect':
    'w-[inherit] text-nowrap font-bold bg-abb-primary-dark border-2 border-abb-primary-dark text-abb-white cursor-not-allowed px-3 py-1 rounded',
  circular: 'border cursor-not-allowed rounded-full justify-center',
  none: '',
};

@Component({
  selector: 'abb-button',
  standalone: true,
  imports: [
    CommonModule,
    MatProgressSpinnerModule,
    AbbIconComponent,
    OverflowTooltipDirective,
  ],
  template: `
    <button
      [id]="id()"
      [type]="type()"
      [disabled]="disabled() || loading()"
      [attr.form]="form()"
      [ngClass]="[
        disabled()
          ? disabledButtonClassMapper[variant()]
          : buttonClassMapper[variant()],
        className(),
        variant().includes('circular') ? sizeCls() + ' ' + colorCls() : '',
        badge() ? 'relative' : ''
      ]"
      class="inline-flex flex-row items-center justify-center"
      (click)="onClick($event)"
    >
      @if(badge()) {
      <span
        class="rounded-full w-3 h-3 border-2 border-abb-white shadow-md shadow-abb-gray-02 bg-abb-primary absolute top-[3px] right-[2px]"
      ></span>
      } @if (loading()) {
      <mat-progress-spinner
        class="mr-2"
        [ngStyle]="
          variant() === 'secondary'
            ? {
                '--mdc-circular-progress-active-indicator-color':
                  'rgb(var(--abb-color-black)/0.2)'
              }
            : {
                '--mdc-circular-progress-active-indicator-color':
                  'rgb(var(--abb-color-white))'
              }
        "
        [diameter]="16"
        [mode]="'indeterminate'"
      ></mat-progress-spinner>
      } @if (!loading() || variant() !== 'circular') { @if(icon()){
      <abb-icon
        [name]="icon()!"
        [size]="iconSize()"
        [externalSize]="iconExternalSize()"
      ></abb-icon>
      }
      <span
        class="content-center max-w-full"
        [ngClass]="clampTooltipClass() ? clampTooltipClass() : 'truncate'"
        overflowTooltip
        [useClampMode]="!!clampTooltipClass()"
      >
        <ng-content></ng-content>
      </span>
      }
    </button>
  `,
  host: {
    class: 'flex items-center',
  },
  styles: ``,
})
export class AbbButtonComponent {
  size = input<'20' | '24' | '32' | '36' | '40' | '48' | '60'>(
    BUTTONSIZE.SIZE36
  );
  color = input<string>(COLOR.ABB_PRIMARY);
  disabled = input<boolean>(false);
  badge = input<boolean>(false);
  className = input<string>('');
  id = input<string>('');
  variant = input<ButtonVariant>('primary');
  type = input<'button' | 'submit' | 'reset' | undefined>('button');
  form = input<string | undefined>(undefined);
  onclick = output<MouseEvent>();
  loading = input<boolean>(false);
  icon = input<Icon | undefined>(undefined);
  iconSize = input<'small' | 'medium' | 'large'>('medium');
  iconExternalSize = input<string | undefined>(undefined);
  clampTooltipClass = input<string | undefined>(undefined);

  readonly buttonClassMapper = buttonClassMapper;
  readonly circularButtonSizeClassMapper = circularButtonSizeClassMapper;
  readonly disabledButtonClassMapper = disabledButtonClassMapper;

  constructor() {}

  sizeCls = computed(
    () => circularButtonSizeClassMapper[this.size() as BUTTONSIZE]
  );

  colorCls = computed(() => circularButtonColorClassMapper[this.color()]);

  onClick = (e: MouseEvent): void => {
    this.onclick.emit(e);
  };
}
